import { useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useLang } from "../i18n";
import { Icon } from "./Icon";
import { MUNICIPALITIES, REGIONS } from "../data/municipalities";
import { SHELTERS } from "../data/shelters";
import { FIRE_STATIONS } from "../data/firestations";
import { HOSPITALS_GEO, CDT_MARKERS } from "../data/hospitalsGeo";

const S = {
  note: {
    es: "Cada marcador muestra el contacto OMME del municipio. Los refugios específicos se anuncian oficialmente cuando NMEAD los activa: confirme llamando a su OMME o al 787-724-0124.",
    en: "Each marker shows the municipality's OMME contact. Specific shelters are officially announced when NMEAD activates them: confirm by calling your OMME or 787-724-0124.",
  },
  designated: { es: "facilidades designadas", en: "designated facilities" },
  lowBw: {
    es: "Mapa desactivado en modo ahorro de datos. Directorio OMME rápido:",
    en: "Map disabled in data-saver mode. Quick OMME directory:",
  },
  shelters: { es: "Refugios: confirme con su OMME", en: "Shelters: confirm with your OMME" },
  callOmme: { es: "Llamar OMME", en: "Call OMME" },
  showFire: { es: "Mostrar estaciones de bomberos", en: "Show fire stations" },
  hideFire: { es: "Ocultar estaciones de bomberos", en: "Hide fire stations" },
  fireNote: { es: "Estación estatal del Cuerpo de Bomberos", en: "State Fire Department station" },
  showHosp: { es: "Mostrar hospitales y CDT", en: "Show hospitals & CDT" },
  hideHosp: { es: "Ocultar hospitales y CDT", en: "Hide hospitals & CDT" },
  cdtApprox: { es: "CDT municipal — marcador en el casco urbano (ubicación aproximada). Emergencias: 911.", en: "Municipal CDT — marker at town center (approximate location). Emergencies: 911." },
  showShelters: { es: "Mostrar refugios designados", en: "Show designated shelters" },
  hideShelters: { es: "Ocultar refugios designados", en: "Hide designated shelters" },
  dirLabel: { es: "Cómo llegar", en: "Directions" },
  shelterNote: { es: "Refugio designado — confirme apertura con su OMME", en: "Designated shelter — confirm opening with your OMME" },
  legendTitle: { es: "Leyenda", en: "Legend" },
  legMuni: { es: "Municipio (contacto OMME)", en: "Municipality (OMME contact)" },
  legShelter: { es: "Refugio designado", en: "Designated shelter" },
  legFire: { es: "Estación de bomberos", en: "Fire station" },
  legHosp: { es: "Hospital", en: "Hospital" },
  legCdt: { es: "CDT (ubicación aprox.)", en: "CDT (approx. location)" },
  emergencies: { es: "Emergencias: 911", en: "Emergencies: 911" },
};

const t = (o, lang) => o[lang] ?? o.es;

const SHELTER_POINTS = Object.entries(SHELTERS).flatMap(([muni, items]) =>
  items.filter((s) => s.lat != null).map((s) => ({ ...s, muni }))
);
const MUNI_BY_NAME = Object.fromEntries(MUNICIPALITIES.map((m) => [m.name, m]));

export function ShelterMap({ lowBandwidth }) {
  const { lang } = useLang();
  const [showFire, setShowFire] = useState(false);
  const [showHosp, setShowHosp] = useState(false);
  const [showShelters, setShowShelters] = useState(false);
  const [loc, setLoc] = useState(null);
  const mapRef = useRef(null);

  const locate = () => {
    if (!navigator.geolocation) { setLoc({ error: true }); return; }
    setLoc({ loading: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setLoc({ pos: p });
        mapRef.current?.setView(p, 13);
      },
      () => setLoc({ error: true }),
      { timeout: 10000 }
    );
  };

  if (lowBandwidth) {
    return (
      <div data-testid="shelter-map-lowbw" className="rounded-lg border border-slate-300 bg-white p-5">
        <p className="mb-4 text-sm font-bold text-slate-600">{t(S.lowBw, lang)}</p>
        <ul className="grid gap-1.5 sm:grid-cols-2 text-sm">
          {MUNICIPALITIES.map((m) => (
            <li key={m.name} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1.5">
              <span className="font-semibold text-slate-800">{m.name}</span>
              <a href={`tel:${m.ommeTel}`} className="font-mono-pr text-xs font-bold text-amber-700">{m.omme}</a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div data-testid="shelter-map">
      <div className="mb-4 rounded-lg border-l-4 border-amber-600 border border-slate-300 bg-amber-50 p-4">
        <p className="flex gap-2.5 text-sm text-amber-900 leading-relaxed">
          <Icon name="Info" className="w-5 h-5 shrink-0 text-amber-700" />
          {t(S.note, lang)}
        </p>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          data-testid="toggle-fire-stations"
          onClick={() => setShowFire((v) => !v)}
          aria-pressed={showFire}
          className={`inline-flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
            showFire ? "border-red-600 bg-red-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-red-500"
          }`}
        >
          <Icon name="Flame" className="w-4 h-4" />
          {showFire ? t(S.hideFire, lang) : t(S.showFire, lang)} ({FIRE_STATIONS.length})
        </button>
        <button
          data-testid="toggle-hospitals"
          onClick={() => setShowHosp((v) => !v)}
          aria-pressed={showHosp}
          className={`inline-flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
            showHosp ? "border-blue-700 bg-blue-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-blue-600"
          }`}
        >
          <Icon name="Cross" className="w-4 h-4" />
          {showHosp ? t(S.hideHosp, lang) : t(S.showHosp, lang)} ({HOSPITALS_GEO.filter((h) => h.lat != null).length + CDT_MARKERS.length})
        </button>
        <button
          data-testid="toggle-shelters-layer"
          onClick={() => setShowShelters((v) => !v)}
          aria-pressed={showShelters}
          className={`inline-flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
            showShelters ? "border-green-700 bg-green-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-green-700"
          }`}
        >
          <Icon name="MapPin" className="w-4 h-4" />
          {showShelters ? t(S.hideShelters, lang) : t(S.showShelters, lang)} ({SHELTER_POINTS.length})
        </button>
        <button
          data-testid="locate-me-btn"
          onClick={locate}
          disabled={loc?.loading}
          className="inline-flex items-center gap-2 rounded-md border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-sky-600 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
        >
          <Icon name="MapPin" className="w-4 h-4" />
          {loc?.loading ? (lang === "es" ? "Localizando…" : "Locating…") : (lang === "es" ? "Centrar en mi ubicación" : "Center on my location")}
        </button>
        {loc?.error && (
          <span data-testid="locate-error" className="self-center text-xs font-semibold text-red-700">
            {lang === "es" ? "No pudimos obtener su ubicación." : "Could not get your location."}
          </span>
        )}
      </div>
      <div className="relative z-0 overflow-hidden rounded-lg border border-slate-300">
        <MapContainer ref={mapRef} center={[18.22, -66.43]} zoom={9} scrollWheelZoom={false} style={{ height: 520, width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {MUNICIPALITIES.map((m) => (
            <CircleMarker
              key={m.name}
              center={[m.lat, m.lng]}
              radius={7}
              pathOptions={{ color: "#1C2B4A", weight: 2, fillColor: "#D97706", fillOpacity: 0.9 }}
            >
              <Popup>
                <div data-testid={`map-popup-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{ minWidth: 170 }}>
                  <strong style={{ fontSize: 14 }}>{m.name}</strong>
                  <div style={{ fontSize: 12, color: "#475569", margin: "2px 0 2px" }}>
                    {t(REGIONS[m.region], lang)} · {(SHELTERS[m.name] || []).length} {t(S.designated, lang)}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748B", marginBottom: 6 }}>{t(S.shelters, lang)}</div>
                  <a href={`tel:${m.ommeTel}`} style={{ display: "block", fontWeight: 700, color: "#B45309", fontSize: 13 }}>
                    OMME: {m.omme}
                  </a>
                  <a href="tel:7877240124" style={{ display: "block", fontWeight: 700, color: "#1C2B4A", fontSize: 13 }}>
                    NMEAD: 787-724-0124
                  </a>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {showFire && FIRE_STATIONS.map((s, i) => (
            <CircleMarker
              key={`fs-${i}`}
              center={[s.lat, s.lng]}
              radius={5}
              pathOptions={{ color: "#FFFFFF", weight: 1.5, fillColor: "#DC2626", fillOpacity: 0.95 }}
            >
              <Popup>
                <div data-testid={`fire-popup-${i}`} style={{ minWidth: 160 }}>
                  <strong style={{ fontSize: 13 }}>{s.name}</strong>
                  <div style={{ fontSize: 12, color: "#475569", margin: "2px 0 6px" }}>
                    {s.town} · {t(S.fireNote, lang)}
                  </div>
                  <a href="tel:7873432330" style={{ display: "block", fontWeight: 700, color: "#DC2626", fontSize: 13 }}>
                    Bomberos: 787-343-2330
                  </a>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {showHosp && HOSPITALS_GEO.filter((h) => h.lat != null).map((h, i) => (
            <CircleMarker
              key={`hosp-${i}`}
              center={[h.lat, h.lng]}
              radius={6}
              pathOptions={{ color: "#FFFFFF", weight: 1.5, fillColor: "#1D4ED8", fillOpacity: 0.95 }}
            >
              <Popup>
                <div data-testid={`hosp-popup-${i}`} style={{ minWidth: 170 }}>
                  <strong style={{ fontSize: 13 }}>{h.name}</strong>
                  <div style={{ fontSize: 12, color: "#475569", margin: "2px 0 6px" }}>{h.town}</div>
                  <a href={`tel:${h.tel || "911"}`} style={{ display: "block", fontWeight: 700, color: "#1D4ED8", fontSize: 13 }}>
                    {h.phone || t(S.emergencies, lang)}
                  </a>
                  {h.tel && (
                    <a data-testid={`hosp-popup-911-${i}`} href="tel:911" style={{ display: "block", fontWeight: 700, color: "#DC2626", fontSize: 13 }}>
                      {t(S.emergencies, lang)}
                    </a>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {showHosp && CDT_MARKERS.map((c, i) => (
            <CircleMarker
              key={`cdt-${i}`}
              center={[c.lat, c.lng]}
              radius={3.5}
              pathOptions={{ color: "#1D4ED8", weight: 1, fillColor: "#93C5FD", fillOpacity: 0.8 }}
            >
              <Popup>
                <strong style={{ fontSize: 13 }}>{c.name}</strong>
                <div style={{ fontSize: 11, color: "#64748B", margin: "2px 0 4px" }}>{t(S.cdtApprox, lang)}</div>
              </Popup>
            </CircleMarker>
          ))}
          {showShelters && SHELTER_POINTS.map((s, i) => (
            <CircleMarker
              key={`sh-${i}`}
              center={[s.lat, s.lng]}
              radius={3.5}
              pathOptions={{ color: "#FFFFFF", weight: 1, fillColor: "#15803D", fillOpacity: 0.85 }}
            >
              <Popup>
                <div data-testid={`shelter-map-popup-${i}`} style={{ minWidth: 180 }}>
                  <strong style={{ fontSize: 13 }}>{s.facility}</strong>
                  {s.address ? <div style={{ fontSize: 12, color: "#475569" }}>{s.address}</div> : null}
                  <div style={{ fontSize: 11, color: "#64748B", margin: "2px 0 6px" }}>
                    {s.muni} · {t(S.shelterNote, lang)}
                  </div>
                  <a href={s.maps} target="_blank" rel="noopener noreferrer" style={{ display: "block", fontWeight: 700, color: "#15803D", fontSize: 13 }}>
                    {t(S.dirLabel, lang)}
                  </a>
                  {MUNI_BY_NAME[s.muni] && (
                    <a href={`tel:${MUNI_BY_NAME[s.muni].ommeTel}`} style={{ display: "block", fontWeight: 700, color: "#B45309", fontSize: 13 }}>
                      OMME: {MUNI_BY_NAME[s.muni].omme}
                    </a>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {loc?.pos && (
            <CircleMarker
              center={loc.pos}
              radius={9}
              pathOptions={{ color: "#FFFFFF", weight: 2.5, fillColor: "#0284C7", fillOpacity: 0.95 }}
            >
              <Popup>
                <strong data-testid="you-are-here" style={{ fontSize: 13 }}>{lang === "es" ? "Está aquí" : "You are here"}</strong>
              </Popup>
            </CircleMarker>
          )}
        </MapContainer>
        <div data-testid="map-legend" className="absolute bottom-3 left-3 z-[1000] rounded-md border border-slate-300 bg-white/95 px-3.5 py-2.5 shadow-md backdrop-blur">
          <p className="mb-1.5 font-mono-pr text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{t(S.legendTitle, lang)}</p>
          <ul className="space-y-1">
            <li className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full border-2 border-[#1C2B4A] bg-[#D97706]" />{t(S.legMuni, lang)}
            </li>
            <li className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full border border-white bg-[#15803D]" />{t(S.legShelter, lang)}
            </li>
            <li className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full border border-white bg-[#DC2626]" />{t(S.legFire, lang)}
            </li>
            <li className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full border border-white bg-[#1D4ED8]" />{t(S.legHosp, lang)}
            </li>
            <li className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="h-3 w-3 rounded-full border border-[#1D4ED8] bg-[#93C5FD]" />{t(S.legCdt, lang)}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
